import { ref, computed } from 'vue';
import { useAlert } from 'dashboard/composables';
import BotDataSourcesAPI from 'dashboard/api/omni/botDataSources';
import KnowledgeBaseAPI from 'dashboard/api/omni/knowledgeBase';

export function useBotDataSources() {
  // State
  const bots = ref([]);
  const selectedBotId = ref(null);
  const dataSources = ref([]);
  const dataEntries = ref([]);
  const stats = ref(null);
  const collections = ref([]);
  const loading = ref(false);
  const uploading = ref(false);
  const processing = ref(false);
  const meta = ref({ page: 1, per_page: 25, total: 0 });

  // Computed
  const selectedBot = computed(() => {
    if (!selectedBotId.value) return null;
    return bots.value.find(b => b.id === selectedBotId.value);
  });

  const hasSources = computed(() => dataSources.value.length > 0);
  const hasEntries = computed(() => dataEntries.value.length > 0);
  const totalPages = computed(() => Math.ceil(meta.value.total / meta.value.per_page));

  // Fetch bots
  const fetchBots = async () => {
    try {
      const { data } = await KnowledgeBaseAPI.getBots();
      bots.value = data;
    } catch (error) {
      useAlert('Erro ao carregar bots');
      console.error(error);
    }
  };

  // Fetch data sources for selected bot
  const fetchSources = async (params = {}) => {
    if (!selectedBotId.value) {
      dataSources.value = [];
      return;
    }

    loading.value = true;
    try {
      const { data } = await BotDataSourcesAPI.getSources(selectedBotId.value, params);
      dataSources.value = data.data_sources || [];
      meta.value = data.meta || meta.value;
    } catch (error) {
      useAlert('Erro ao carregar fontes de dados');
      console.error(error);
    } finally {
      loading.value = false;
    }
  };

  // Fetch a single source and update it in the list (lightweight, no loading state)
  const fetchSource = async (sourceId) => {
    if (!selectedBotId.value) return null;

    try {
      const { data } = await BotDataSourcesAPI.getSource(selectedBotId.value, sourceId);
      const index = dataSources.value.findIndex(s => s.id === sourceId);
      if (index !== -1) {
        dataSources.value[index] = { ...dataSources.value[index], ...data };
      }
      return data;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  // Upload new file
  const uploadFile = async (file, collection = null) => {
    if (!selectedBotId.value) {
      useAlert('Selecione um bot primeiro');
      return null;
    }

    uploading.value = true;
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (collection) formData.append('collection', collection);

      const { data } = await BotDataSourcesAPI.createSource(selectedBotId.value, formData);
      dataSources.value = [data, ...dataSources.value];
      useAlert('Arquivo enviado com sucesso!');
      return data;
    } catch (error) {
      const message = error.response?.data?.errors?.join(', ') || 'Erro ao enviar arquivo';
      useAlert(message);
      console.error(error);
      return null;
    } finally {
      uploading.value = false;
    }
  };

  // Update an existing API source
  const updateApiSource = async (sourceId, payload) => {
    if (!selectedBotId.value) return null;
    try {
      const { data } = await BotDataSourcesAPI.updateApiSource(selectedBotId.value, sourceId, payload);
      const index = dataSources.value.findIndex(s => s.id === sourceId);
      if (index !== -1) dataSources.value[index] = { ...dataSources.value[index], ...data };
      useAlert('Fonte de API atualizada com sucesso!');
      return data;
    } catch (error) {
      const message = error.response?.data?.errors?.join(', ') || 'Erro ao atualizar fonte de API';
      useAlert(message);
      console.error(error);
      return null;
    }
  };

  // Create a new API source
  const createApiSource = async (payload) => {
    if (!selectedBotId.value) {
      useAlert('Selecione um bot primeiro');
      return null;
    }

    uploading.value = true;
    try {
      const { data } = await BotDataSourcesAPI.createApiSource(selectedBotId.value, {
        source_type: 'api',
        ...payload,
      });
      dataSources.value = [data, ...dataSources.value];
      useAlert('Fonte de API criada com sucesso!');
      return data;
    } catch (error) {
      const message = error.response?.data?.errors?.join(', ') || 'Erro ao criar fonte de API';
      useAlert(message);
      console.error(error);
      return null;
    } finally {
      uploading.value = false;
    }
  };

  // Test an API configuration (before saving)
  const testApi = async (payload) => {
    if (!selectedBotId.value) return null;
    try {
      const { data } = await BotDataSourcesAPI.testApi(selectedBotId.value, payload);
      return data;
    } catch (error) {
      const message = error.response?.data?.error || 'Erro ao testar API';
      return { success: false, error: message };
    }
  };

  // Generate extraction config via AI
  const generateExtraction = async (sampleJson, extractionPrompt) => {
    if (!selectedBotId.value) return null;
    try {
      const { data } = await BotDataSourcesAPI.generateExtraction(selectedBotId.value, {
        sample_json: sampleJson,
        extraction_prompt: extractionPrompt,
      });
      return data;
    } catch (error) {
      const message = error.response?.data?.error || 'Erro ao gerar configuração';
      return { error: message };
    }
  };

  // Preview extraction results
  const previewExtraction = async (sampleJson, extractionConfig) => {
    if (!selectedBotId.value) return null;
    try {
      const { data } = await BotDataSourcesAPI.previewExtraction(selectedBotId.value, {
        sample_json: sampleJson,
        extraction_config: extractionConfig,
      });
      return data;
    } catch (error) {
      const message = error.response?.data?.error || 'Erro ao gerar preview';
      return { error: message };
    }
  };

  // Trigger a manual sync for an API source
  const syncSource = async (sourceId) => {
    if (!selectedBotId.value) return;

    processing.value = true;
    try {
      const { data } = await BotDataSourcesAPI.syncSource(selectedBotId.value, sourceId);
      const index = dataSources.value.findIndex(s => s.id === sourceId);
      if (index !== -1) {
        dataSources.value[index] = { ...dataSources.value[index], ...data.data_source };
      }
      useAlert('Sincronização iniciada');
      return data;
    } catch (error) {
      const message = error.response?.data?.error || 'Erro ao sincronizar';
      useAlert(message);
      console.error(error);
    } finally {
      processing.value = false;
    }
  };

  // Start processing a source
  const processSource = async (sourceId) => {
    if (!selectedBotId.value) return;

    processing.value = true;
    try {
      const { data } = await BotDataSourcesAPI.processSource(selectedBotId.value, sourceId);
      const index = dataSources.value.findIndex(s => s.id === sourceId);
      if (index !== -1) {
        dataSources.value[index] = data.data_source;
      }
      useAlert('Processamento iniciado');
      return data;
    } catch (error) {
      const message = error.response?.data?.error || 'Erro ao iniciar processamento';
      useAlert(message);
      console.error(error);
    } finally {
      processing.value = false;
    }
  };

  // Reprocess a source
  const reprocessSource = async (sourceId) => {
    if (!selectedBotId.value) return;

    processing.value = true;
    try {
      const { data } = await BotDataSourcesAPI.reprocessSource(selectedBotId.value, sourceId);
      const index = dataSources.value.findIndex(s => s.id === sourceId);
      if (index !== -1) {
        dataSources.value[index] = data.data_source;
      }
      useAlert('Reprocessamento iniciado');
      return data;
    } catch (error) {
      const message = error.response?.data?.error || 'Erro ao reprocessar';
      useAlert(message);
      console.error(error);
    } finally {
      processing.value = false;
    }
  };

  // Delete a source
  const deleteSource = async (sourceId) => {
    if (!selectedBotId.value) return;

    try {
      await BotDataSourcesAPI.deleteSource(selectedBotId.value, sourceId);
      dataSources.value = dataSources.value.filter(s => s.id !== sourceId);
      useAlert('Fonte de dados excluída');
    } catch (error) {
      useAlert('Erro ao excluir fonte de dados');
      console.error(error);
    }
  };

  // Fetch data entries
  const fetchEntries = async (params = {}) => {
    if (!selectedBotId.value) {
      dataEntries.value = [];
      return;
    }

    loading.value = true;
    try {
      const cleanParams = Object.fromEntries(
        Object.entries(params).filter(([, v]) => v != null && v !== '')
      );
      const { data } = await BotDataSourcesAPI.getEntries(selectedBotId.value, cleanParams);
      dataEntries.value = data.entries || [];
      meta.value = data.meta || meta.value;
    } catch (error) {
      useAlert('Erro ao carregar entradas');
      console.error(error);
    } finally {
      loading.value = false;
    }
  };

  // Delete an entry
  const deleteEntry = async (entryId) => {
    if (!selectedBotId.value) return;

    try {
      await BotDataSourcesAPI.deleteEntry(selectedBotId.value, entryId);
      dataEntries.value = dataEntries.value.filter(e => e.id !== entryId);
      useAlert('Entrada excluída');
      await fetchStats();
    } catch (error) {
      useAlert('Erro ao excluir entrada');
      console.error(error);
    }
  };

  // Bulk delete entries
  const bulkDeleteEntries = async (params) => {
    if (!selectedBotId.value) return;

    try {
      const { data } = await BotDataSourcesAPI.bulkDeleteEntries(selectedBotId.value, params);
      useAlert(`${data.deleted_count} entradas excluídas`);
      await fetchEntries({ collection: params.collection === undefined ? null : undefined });
      await fetchStats();
      await fetchCollections();
      return data;
    } catch (error) {
      useAlert('Erro ao excluir entradas');
      console.error(error);
    }
  };

  // Search entries
  const searchEntries = async (query, params = {}) => {
    if (!selectedBotId.value || !query) return [];

    loading.value = true;
    try {
      const { data } = await BotDataSourcesAPI.searchEntries(selectedBotId.value, query, params);
      return data.results || [];
    } catch (error) {
      useAlert('Erro na busca');
      console.error(error);
      return [];
    } finally {
      loading.value = false;
    }
  };

  // Fetch stats
  const fetchStats = async () => {
    if (!selectedBotId.value) {
      stats.value = null;
      return;
    }

    try {
      const { data } = await BotDataSourcesAPI.getEntryStats(selectedBotId.value);
      stats.value = data;
    } catch (error) {
      console.error(error);
    }
  };

  // Fetch collections
  const fetchCollections = async () => {
    if (!selectedBotId.value) {
      collections.value = [];
      return;
    }

    try {
      const { data } = await BotDataSourcesAPI.getEntryCollections(selectedBotId.value);
      collections.value = data.collections || [];
    } catch (error) {
      console.error(error);
    }
  };

  // Select a bot
  const selectBot = async (botId) => {
    selectedBotId.value = botId;
    await Promise.all([fetchSources(), fetchStats(), fetchCollections()]);
  };

  // Change page
  const changePage = async (page, additionalParams = {}) => {
    await fetchSources({ page, ...additionalParams });
  };

  return {
    // State
    bots,
    selectedBotId,
    selectedBot,
    dataSources,
    dataEntries,
    stats,
    collections,
    loading,
    uploading,
    processing,
    meta,

    // Computed
    hasSources,
    hasEntries,
    totalPages,

    // Methods
    fetchBots,
    fetchSources,
    fetchSource,
    uploadFile,
    createApiSource,
    updateApiSource,
    testApi,
    generateExtraction,
    previewExtraction,
    syncSource,
    processSource,
    reprocessSource,
    deleteSource,
    fetchEntries,
    deleteEntry,
    bulkDeleteEntries,
    searchEntries,
    fetchStats,
    fetchCollections,
    selectBot,
    changePage,
  };
}

